// components/AdminManualClockActions.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    Snackbar
} from "@mui/material";
import {
    AccessTime as AccessTimeIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

const AdminManualClockActions = ({ orgId }) => {
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const employeeData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setEmployees(employeeData);
        } catch (err) {
            console.error("Error fetching employees:", err.message);
            setError("Failed to load employees. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleEmployeeChange = (event) => {
        setSelectedEmployee(event.target.value);
    };

    // Fix in AdminManualClockActions.js
    const handleClockAction = async (action) => {
        if (!selectedEmployee) {
            setError("Please select an employee");
            setOpenSnackbar(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const employee = employees.find(emp => emp.id === selectedEmployee);

            // Current time as a JavaScript Date object (not serverTimestamp)
            const currentTime = new Date();

            // Add manual clock log to Firestore
            const logData = {
                userId: selectedEmployee,
                email: employee.email,
                type: action,
                timestamp: serverTimestamp(), // Only use serverTimestamp for top-level fields
                manualEntry: true,
                enteredBy: "admin",
                notes: `Manual ${action === "clockIn" ? "clock in" : "clock out"} by administrator`,
                manualEntryTime: currentTime.toISOString() // Store string representation instead
            };

            await addDoc(collection(db, "organizations", orgId, "timeLogs"), logData);

            setSuccess(`Successfully ${action === "clockIn" ? "clocked in" : "clocked out"} ${employee.name || employee.email}`);
            setOpenSnackbar(true);
        } catch (err) {
            console.error("Error performing manual clock action:", err.message);
            setError(`Failed to ${action === "clockIn" ? "clock in" : "clock out"} employee. Please try again.`);
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Manual Clock Actions
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
                Use this tool to manually clock employees in or out when needed.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormControl fullWidth disabled={loading}>
                        <InputLabel>Select Employee</InputLabel>
                        <Select
                            value={selectedEmployee}
                            onChange={handleEmployeeChange}
                            label="Select Employee"
                        >
                            <MenuItem value="">
                                <em>Select an employee</em>
                            </MenuItem>
                            {employees.map((employee) => (
                                <MenuItem key={employee.id} value={employee.id}>
                                    {employee.name || employee.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<ArrowUpwardIcon />}
                            onClick={() => handleClockAction("clockIn")}
                            disabled={loading || !selectedEmployee}
                            sx={{ flex: 1 }}
                        >
                            {loading ? <CircularProgress size={24} /> : "Clock In Employee"}
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<ArrowDownwardIcon />}
                            onClick={() => handleClockAction("clockOut")}
                            disabled={loading || !selectedEmployee}
                            sx={{ flex: 1 }}
                        >
                            {loading ? <CircularProgress size={24} /> : "Clock Out Employee"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={error ? "error" : "success"}
                    sx={{ width: '100%' }}
                >
                    {error || success}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default AdminManualClockActions;